// =============================================================================
// Owner context -- the agent-web's "which owner am I acting for right now"
// state machine.
//
// Precedence (highest -> lowest):
//   1. ?as=<id> in the URL
//   2. localStorage["vandiez.agent.current_owner_id"]
//   3. First active grant from GET /agents/me/owners
//
// Behaviour:
//   * On every mount we hydrate the owners list via the API.
//   * On every URL change, the URL's ?as= wins if it matches an active grant.
//   * If the URL has ?as= for an owner that's been revoked, we drop the
//     param + redirect to /.
// =============================================================================

import type React from "react";
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";
import type { ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { agentsApi } from "@/api/client";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";
import type { AgentOwnerSummary } from "@/types/agent";

const STORAGE_KEY = "vandiez.agent.current_owner_id";

interface OwnerContextValue {
  owners: AgentOwnerSummary[];
  currentOwnerId: string | null;
  currentOwner: AgentOwnerSummary | null;
  isLoading: boolean;
  error: string | null;
  setOwner: (ownerId: string) => void;
  refresh: () => Promise<void>;
}

const OwnerCtx = createContext<OwnerContextValue | null>(null);

export function OwnerContextProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const { accessToken } = useAuth();
  const [owners, setOwners] = useState<AgentOwnerSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const readStored = (): string | null => {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  };

  const writeStored = (id: string | null): void => {
    try {
      if (id === null) {
        window.localStorage.removeItem(STORAGE_KEY);
      } else {
        window.localStorage.setItem(STORAGE_KEY, id);
      }
    } catch {
      // ignore
    }
  };

  const refresh = useCallback(async (): Promise<void> => {
    if (accessToken === null) {return;}
    setIsLoading(true);
    setError(null);
    try {
      const resp = await agentsApi.myOwners(accessToken);
      setOwners(resp.items);
      setIsLoading(false);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.problem.detail : "Couldn't load your owners.",
      );
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const urlAs = searchParams.get("as");

  // Compute the current owner id by precedence: URL > localStorage > first active.
  const currentOwnerId = useMemo<string | null>(() => {
    const activeIds = new Set(owners.filter((o) => o.status === "active").map((o) => o.owner_id));
    if (urlAs !== null && activeIds.has(urlAs)) {return urlAs;}
    const stored = readStored();
    if (stored !== null && activeIds.has(stored)) {return stored;}
    const first = owners.find((o) => o.status === "active");
    return first?.owner_id ?? null;
  }, [owners, urlAs]);

  const currentOwner = useMemo(
    () => owners.find((o) => o.owner_id === currentOwnerId) ?? null,
    [owners, currentOwnerId],
  );

  // If URL ?as= points at a revoked / unknown owner, drop it + redirect.
  useEffect(() => {
    if (urlAs === null) {return;}
    if (owners.length === 0) {return;}
    const activeIds = new Set(owners.filter((o) => o.status === "active").map((o) => o.owner_id));
    if (!activeIds.has(urlAs)) {
      const next = new URLSearchParams(searchParams);
      next.delete("as");
      setSearchParams(next, { replace: true });
      navigate("/", { replace: true });
    }
  }, [urlAs, owners, navigate, searchParams, setSearchParams]);

  // Persist whatever the resolved currentOwnerId is for next visit.
  useEffect(() => {
    if (currentOwnerId !== null) {
      writeStored(currentOwnerId);
    }
  }, [currentOwnerId]);

  const setOwner = useCallback((ownerId: string): void => {
    const next = new URLSearchParams(searchParams);
    next.set("as", ownerId);
    setSearchParams(next, { replace: false });
    writeStored(ownerId);
  }, [searchParams, setSearchParams]);

  const value = useMemo<OwnerContextValue>(
    () => ({
      owners, currentOwnerId, currentOwner, isLoading, error, setOwner, refresh,
    }),
    [owners, currentOwnerId, currentOwner, isLoading, error, setOwner, refresh],
  );

  return <OwnerCtx.Provider value={value}>{children}</OwnerCtx.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOwnerContext(): OwnerContextValue {
  const ctx = useContext(OwnerCtx);
  if (ctx === null) {
    throw new Error("useOwnerContext() must be used inside <OwnerContextProvider>.");
  }
  return ctx;
}
